import { ArrowRight, BrainCircuit, CheckCircle2, Loader2, Sparkles, Target } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  getStudentCareerProfile,
  recommendStudentCareers,
  selectStudentCareerRecommendation,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { setPageMetadata } from '../page-metadata.js'

const initialForm = {
  interests: '',
  personalityTendency: '',
  major: '',
  grade: '',
  skillLevel: '',
}

const interestOptions = ['编程开发', '数据分析', 'AI工具', '产品运营', '视觉设计', '内容表达', '用户研究', '商业分析']
const tendencyOptions = [
  '喜欢拆解复杂问题',
  '喜欢做可视化和表达',
  '喜欢和人沟通协作',
  '喜欢做产品和用户分析',
  '喜欢动手做项目',
  '喜欢稳定清晰的路径',
]
const gradeOptions = ['大一', '大二', '大三', '大四', '研一', '研二', '研三', '已毕业']
const levelOptions = ['零基础', '学过基础', '做过课程项目', '有实习或项目经验', '正在求职准备']

function profileToForm(profile) {
  if (!profile) return initialForm
  return {
    interests: Array.isArray(profile.interests) ? profile.interests.join('、') : '',
    personalityTendency: profile.personalityTendency || '',
    major: profile.major || '',
    grade: profile.grade || '',
    skillLevel: profile.skillLevel || '',
  }
}

function toggleInterest(value, form, setForm) {
  const current = form.interests
    .split(/[,\n，、]/)
    .map((item) => item.trim())
    .filter(Boolean)
  const next = current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value]
  setForm((state) => ({ ...state, interests: next.join('、') }))
}

export default function StudentCareerExplorePage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState(initialForm)
  const [recommendations, setRecommendations] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectingId, setSelectingId] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    setPageMetadata({
      title: '职业探索 - 青途 AI',
      description: '通过学习和职业偏好分析，发现适合自己的职业方向，再生成个性化成长路线。',
    })
  }, [])

  useEffect(() => {
    if (auth?.loading || !auth?.user) return undefined
    const controller = new AbortController()
    setLoading(true)
    getStudentCareerProfile(controller.signal)
      .then((payload) => setForm(profileToForm(payload?.profile)))
      .catch(() => {})
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [auth?.loading, auth?.user])

  function updateField(key, value) {
    setError('')
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function submitTest(event) {
    event.preventDefault()
    if (!auth?.user) {
      setError('请先登录后进行职业探索。')
      return
    }
    if (!form.interests || !form.personalityTendency || !form.major || !form.grade || !form.skillLevel) {
      setError('请完整填写兴趣、性格倾向、专业、年级和技能水平。')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const payload = await recommendStudentCareers({
        ...form,
        interests: form.interests.split(/[,\n，、]/).map((item) => item.trim()).filter(Boolean),
      })
      setRecommendations(Array.isArray(payload?.recommendations) ? payload.recommendations : [])
    } catch (err) {
      setError(err?.message || '职业推荐生成失败，请稍后重试。')
    } finally {
      setSubmitting(false)
    }
  }

  async function chooseCareer(item) {
    setSelectingId(item.id)
    setError('')
    try {
      await selectStudentCareerRecommendation(item.id)
      navigate('/learning')
    } catch (err) {
      setError(err?.message || '选择职业方向失败，请稍后重试。')
    } finally {
      setSelectingId('')
    }
  }

  if (auth?.loading || loading) {
    return (
      <main className="page-container career-explore-page">
        <section className="career-explore-hero"><span><Loader2 size={18} /> 正在加载职业探索...</span></section>
      </main>
    )
  }

  if (!auth?.user) {
    return (
      <main className="page-container career-explore-page">
        <section className="career-explore-hero">
          <span><Sparkles size={16} /> 职业探索 V1</span>
          <h1>登录后发现适合你的职业方向</h1>
          <p>先做学习和职业偏好分析，再生成个性化成长路线。</p>
          <Link className="primary-button" to="/login">去登录 <ArrowRight size={16} /></Link>
        </section>
      </main>
    )
  }

  const selectedInterests = form.interests.split(/[,\n，、]/).map((item) => item.trim()).filter(Boolean)

  return (
    <main className="page-container career-explore-page">
      <section className="career-explore-hero">
        <div>
          <span><BrainCircuit size={16} /> 职业探索 V1</span>
          <h1>先找到适合你的职业方向，再生成学习路线</h1>
          <p>这不是娱乐 MBTI，而是围绕兴趣、学习方式、专业背景和技能水平做职业偏好分析。</p>
        </div>
      </section>

      <form className="career-explore-form" onSubmit={submitTest}>
        <section>
          <h2>兴趣方向</h2>
          <div className="career-explore-choice-grid">
            {interestOptions.map((item) => (
              <button className={selectedInterests.includes(item) ? 'is-active' : ''} key={item} type="button" onClick={() => toggleInterest(item, form, setForm)}>
                {item}
              </button>
            ))}
          </div>
        </section>

        <label>
          <span>性格倾向 / 学习偏好</span>
          <select value={form.personalityTendency} onChange={(event) => updateField('personalityTendency', event.target.value)}>
            <option value="">请选择</option>
            {tendencyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>

        <label>
          <span>专业</span>
          <input value={form.major} onChange={(event) => updateField('major', event.target.value)} placeholder="例如：软件工程、会计学、视觉传达" />
        </label>

        <label>
          <span>年级</span>
          <select value={form.grade} onChange={(event) => updateField('grade', event.target.value)}>
            <option value="">请选择</option>
            {gradeOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>

        <label>
          <span>技能水平</span>
          <select value={form.skillLevel} onChange={(event) => updateField('skillLevel', event.target.value)}>
            <option value="">请选择</option>
            {levelOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>

        {error ? <p className="student-profile-error">{error}</p> : null}

        <div className="student-profile-actions">
          <button className="primary-button" type="submit" disabled={submitting}>
            {submitting ? '正在分析...' : '生成职业推荐'} <ArrowRight size={16} />
          </button>
        </div>
      </form>

      {recommendations.length ? (
        <section className="career-explore-results">
          <header>
            <span><Target size={16} /> 推荐方向 TOP3</span>
            <h2>选择一个方向后，将生成对应成长路线</h2>
          </header>
          <div className="career-explore-result-grid">
            {recommendations.map((item) => (
              <article key={item.id}>
                <div className="career-explore-score">
                  <strong>{item.matchScore}%</strong>
                  <span>匹配度</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.reason}</p>
                <div>
                  <b>所需能力</b>
                  {item.requiredAbilities?.map((ability) => <small key={ability}>{ability}</small>)}
                </div>
                <div>
                  <b>学习建议</b>
                  {item.learningAdvice?.map((advice) => <small key={advice}>{advice}</small>)}
                </div>
                <button className="primary-button" type="button" disabled={selectingId === item.id} onClick={() => chooseCareer(item)}>
                  {selectingId === item.id ? '生成路线中...' : '选择并生成路线'} <CheckCircle2 size={16} />
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  )
}
